// Convert a path containing template values (e.g. a/b:c) into its
// structural equivalent (e.g. a/b)
export const getStructuralPath = (path: string) => {
  return path
    .split('/')
    .map((part) => part.split(':')[0])
    .join('/');
};

/**
 * Given a path, this function will return a map of account paths and their
 * instantiated values.
 *
 * For example: Given a path a:1/b/c:2/d, getInstanceValueByAccountPath
 * will return:
 * Map<{
 *   a: 1,
 *   a:1/b: undefined,
 *   a:1/b/c: 2,
 *   a:1/b/c:2/d: undefined
 * }>
 */
export const getInstanceValueByAccountPath = (path: string) => {
  const parts = path.split('/');
  const result = new Map<string, string>();

  let workingSet = '';
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const [templateKey, templateValue] = part.split(':');
    workingSet = workingSet ? `${workingSet}/${templateKey}` : templateKey;
    result.set(workingSet, templateValue);
    if (templateValue) {
      workingSet = `${workingSet}:${templateValue}`;
    }
  }

  return result;
};

/**
 * Given a path, this function will return an array of all subpaths.
 * ex: a/b:1/c -> [a, a/b:1, a/b:1/c]
 *
 * This is useful for operations requiring queries on the ancestry chain.
 */
export const getSubpaths = (path: string) =>
  path.split('/').reduce((acc, part) => {
    const prev = acc[acc.length - 1];
    const subPath = prev ? `${prev}/${part}` : part;
    acc.push(subPath);
    return acc;
  }, [] as string[]);

/**
 * Given a path, this function will return an array of all structural subpaths.
 * ex: a/b:1/c -> [a, a/b, a/b/c]
 *
 * This is useful for keying into the Chart of Accounts map.
 */
export const getStructuralSubpaths = (path: string) =>
  getSubpaths(getStructuralPath(path));

/**
 * Given a path, this function will return the template key of the account.
 */
export const getAccountTemplateKey = (path: string): string => {
  const parts = path.split('/');
  const lastPart = parts[parts.length - 1];
  const [, templateKey] = lastPart.split(':');
  return templateKey;
};

/**
 * Given a path, this function will return a list of (key, value) pairs corresponding to each
 * template account in the path.
 *
 * ex: a/b:1/c/d:2 -> [[b, 1], [d, 2]]
 */
export const getTemplateKeyValuePairs = (path: string) =>
  path
    .split('/')
    .filter((token) => token.includes(':'))
    .map((token) => token.split(':'));

/**
 * Given two paths, this function will merge the two paths together, preserving the template
 * values from the old path. This is useful when parts of a path have changed, but entries referencing
 * the old path need to be updated.
 * @param oldTemplatePath The old template path, possibly including named parameters
 * @param newTemplatePath The new template path.
 * @returns An updated path with the template values from the old path.
 */
export const mergePath = (oldTemplatePath: string, newTemplatePath: string) => {
  const oldTemplateParts = oldTemplatePath.split('/');
  const newTemplateParts = newTemplatePath.split('/');
  if (oldTemplateParts.length !== newTemplateParts.length) {
    // we can't do a clean merge if the paths are different lengths
    return newTemplatePath;
  }

  const mergedTemplateParts = oldTemplateParts.map((oldPart, i) => {
    const newPart = newTemplateParts[i];
    if (newPart.includes(':') && oldPart.includes(':')) {
      const newKey = newPart.split(':')[0];
      const oldValue = oldPart.split(':')[1];
      return `${newKey}:${oldValue}`;
    }
    return newPart;
  });

  return mergedTemplateParts.join('/');
};

export const isTemplateAncestor = (templatePath: string) =>
  templatePath.includes(':');

/**
 * Given a template path, get its parent's templatePath
 * ex:
 * - a:{{a_id}}/b:{{b_id}}/c -> a:{{a_id}}/b:{{b_id}}
 * - a -> ''
 */
export const getParentTemplatePath = (templatePath: string) => {
  const parts = templatePath.split('/');
  return parts.slice(0, -1).join('/');
};

/**
 * Given an account path, return the last element in the path.
 * ex: a/b/c -> c
 */
export const getLastPathElement = (path: string) => {
  const parts = path.split('/');
  return parts[parts.length - 1];
};

/**
 * Given an account's template path, instantiated path value, and
 * parameter name, extract the value of the parameter.
 *
 * ex:
 *  templatePath: 'a/b:{{c}}/d:{{e}}',
 *  templatePathWithInstanceValues: 'a/b:1/d:2',
 *  paramName: 'c'
 *  - returns '1'.
 */
export const extractParameterValue = (
  templatePath: string,
  templatePathWithInstanceValues: string,
  paramName: string
): string | null => {
  // Create a regex pattern from the template path
  // Replace all parameters with capture groups
  const paramRegex = /\{\{([^}]+)\}\}/g;
  let regexPattern = templatePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape special regex chars first

  // Replace each parameter with a capture group
  const paramNames: string[] = [];
  regexPattern = regexPattern.replace(/\\\{\\\{([^}]+)\\\}\\\}/g, (m, name) => {
    paramNames.push(name);
    return '([^/]+)'; // Capture everything except forward slash
  });

  // Match the actual path against the pattern
  const regex = new RegExp(`^${regexPattern}$`);
  const m = templatePathWithInstanceValues.match(regex);

  if (!m) {
    return null;
  }

  // Find the index of the parameter we're looking for
  const paramIndex = paramNames.indexOf(paramName);
  if (paramIndex === -1) {
    return null;
  }

  // Return the captured value (+1 because match[0] is the full match)
  return m[paramIndex + 1] || null;
};
